import React from "react"
import PropTypes from "prop-types"
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material"
import i18n from "i18n"
import { isValidIban, normalizeIban } from "../utils/invoiceUtils"

// Only a transfer carries an account: `iban` is ours, the one a customer pays into.
// A direct debit would need the customer's own account, which the invoice model does
// not have, so it gets no field until that is added.
const METHODS_WITH_ACCOUNT = ["transfer"]

// Read-only mirror of the customer's mandate, which is edited on the company record.
const SEPA_FIELDS = [
  { field: "accountNumber", labelKey: "contacts.accountNumber" },
  { field: "mandateRef", labelKey: "contacts.mandateRef" },
  { field: "creditorId", labelKey: "contacts.creditorId" },
]

const InvoicePayment = ({
  invoiceData,
  setInvoiceData,
  issued = false,
  hasValidationErrors = false,
  contability = null,
  onPaymentMethodEdited = () => {},
}) => {
  const handlePaymentMethod = e => {
    const paymentMethod = e.target.value
    onPaymentMethodEdited()
    setInvoiceData(prev => {
      // Drop a stale account when the new method has none, or it would travel in the
      // payload and resurface on the PDF of a cash invoice.
      const iban = METHODS_WITH_ACCOUNT.includes(paymentMethod) ? prev.iban : ""
      return { ...prev, paymentMethod, iban }
    })
  }
  const handleIban = e => {
    // Kept verbatim while typing so an IBAN can be entered in readable groups; the
    // blur normalises it, and save/issue normalise again since the blur may not fire.
    const iban = e.target.value
    setInvoiceData(prev => {
      return { ...prev, iban }
    })
  }
  const handleIbanBlur = () =>
    setInvoiceData(prev => ({ ...prev, iban: normalizeIban(prev.iban) }))
  const handleStatus = e => {
    setInvoiceData(prev => {
      return { ...prev, paymentStatus: e.target.value }
    })
  }

  const isPaymentMethodEmpty = !invoiceData.paymentMethod
  const showPaymentMethodError = hasValidationErrors && isPaymentMethodEmpty
  const showAccount = METHODS_WITH_ACCOUNT.includes(invoiceData.paymentMethod)
  const showSepaData = invoiceData.paymentMethod === "directDebit"
  const ibanIsInvalid = !!invoiceData.iban && !isValidIban(invoiceData.iban)

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {i18n.t("invoices.paymentMethod")}
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={4}>
          <FormControl
            fullWidth
            error={showPaymentMethodError}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: issued ? "#f5f5f5" : "inherit",
              },
              "& .MuiSelect-icon": {
                color: issued ? "transparent" : "inherit",
              },
            }}
          >
            <InputLabel id="payment-method-label">
              {i18n.t("invoices.paymentForm")}
            </InputLabel>
            <Select
              labelId="payment-method-label"
              value={invoiceData.paymentMethod || ""}
              label={i18n.t("invoices.paymentForm")}
              onChange={handlePaymentMethod}
              sx={{ pointerEvents: issued ? "none" : "auto" }}
            >
              <MenuItem value="transfer">
                {i18n.t("invoices.transfer")}
              </MenuItem>
              <MenuItem value="directDebit">
                {i18n.t("invoices.directDebit")}
              </MenuItem>
              <MenuItem value="cash">{i18n.t("invoices.cash")}</MenuItem>
              <MenuItem value="card">{i18n.t("invoices.card")}</MenuItem>
              <MenuItem value="check">{i18n.t("invoices.check")}</MenuItem>
              <MenuItem value="other">{i18n.t("invoices.other")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {showAccount && (
          <Grid item xs={4}>
            <TextField
              label="IBAN"
              variant="outlined"
              fullWidth
              value={invoiceData.iban || ""}
              onChange={handleIban}
              onBlur={handleIbanBlur}
              error={ibanIsInvalid}
              helperText={ibanIsInvalid ? i18n.t("invoices.ibanInvalid") : ""}
              inputProps={{
                readOnly: issued,
                style: issued
                  ? { pointerEvents: "none", background: "#f5f5f5" }
                  : {},
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: issued ? "#f5f5f5" : "inherit",
                },
              }}
            />
          </Grid>
        )}
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel id="status-label">
              {i18n.t("invoices.status")}
            </InputLabel>
            <Select
              labelId="status-label"
              value={invoiceData.paymentStatus || ""}
              label={i18n.t("invoices.status")}
              onChange={handleStatus}
            >
              <MenuItem value="unpaid">{i18n.t("invoices.unpaid")}</MenuItem>
              <MenuItem value="paid">{i18n.t("invoices.paid")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {showSepaData && (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {SEPA_FIELDS.map(({ field, labelKey }) => (
            <Grid item xs={4} key={field}>
              <TextField
                label={i18n.t(labelKey)}
                variant="outlined"
                fullWidth
                value={contability?.[field] || ""}
                inputProps={{
                  readOnly: true,
                  style: { pointerEvents: "none", background: "#f5f5f5" },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { backgroundColor: "#f5f5f5" },
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  )
}

InvoicePayment.propTypes = {
  invoiceData: PropTypes.object.isRequired,
  setInvoiceData: PropTypes.func.isRequired,
  issued: PropTypes.bool,
  hasValidationErrors: PropTypes.bool,
  contability: PropTypes.object,
  onPaymentMethodEdited: PropTypes.func,
}

export default InvoicePayment
